import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";

import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";

import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { useFetchMock } from "src/mock_data/useMockData";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { ActionsType, ColumnsType } from "src/interfaces/Common";
import { useFetchTable } from "@hooks/useFetchDataTable";
import ButtonBackPage from "@components/ButtonBackPage";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const DistributionFe = () => {
  const { t } = useTranslation(["distribution-fe-page"]);
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["sw-distribution-fe"]}`;

  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  let newTags = {};
  if (filters[filterId]?.tags?.length > 0) {
    newTags = {
      tags: filters[filterId].tags
        ?.map((type: any) => {
          return (type?.value).toString().toUpperCase();
        })
        .join(","),
    };
  }

  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    `${ServicesURL.getVersioning}?type=FE_DISTRIBUTIONS`,
    PagesID["sw-distribution-fe"],
    filters[filterId]
      ? { ...filters[filterId], ...newTags }
      : { pageIndex: 0, pageSize: 20 },
  );

  const { dataTable: dataServices } = useFetchTable(
    `${ServicesURL.getVersioningTagging}`,
    PagesID["sw-tagging"],
  );

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.getCertificates}`,
    (url: string) => {
      return fetcher<any>(`${url}`, MethodType.DELETE);
    },
  );

  const configurationColumnOptions: ColumnsType[] | undefined =
    React.useMemo(() => {
      if (dataServices && dataServices?.response) {
        let allTags = dataServices?.response.map((response: any) => {
          return { label: response.tagName, value: response.tagId };
        });
        allTags = allTags.sort((a: any, b: any) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
          if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
          return 0;
        });
        return columns?.map((column: any) => {
          switch (column.accessorKey) {
            case "tags": {
              if (allTags.length > 0) {
                return {
                  ...column,
                  options: allTags,
                };
              } else {
                return {
                  ...column,
                  hasFilter: false,
                };
              }
            }
            default:
              return column;
          }
        });
      } else {
        return columns;
      }
    }, [columns, dataTable]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "link": {
          return {
            ...action,

            callBack: (row: any) => {
              navigate(`${row.versioningId}/tag-association`);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["sw-distribution-fe"]}
            data={dataTable?.response || []}
            columns={configurationColumnOptions || []}
            configurationTopToolbar={actionsTopToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default DistributionFe;
